<script lang="ts" setup>
import { CATALOG_PAGE_TYPES } from '~/constants/catalogPageTypes';
import type { Brand, BrandPageResponse } from '~/types/Brand';

const route = useRoute();
const catalogStore = useCatalogStore();
const brandPageResponse = ref<BrandPageResponse | null>(null);

const data = await catalogStore.getBrandBySlug();

await processData(data);

async function processData(data: BrandPageResponse | null) {
  if (data) {
    if (import.meta.client) setTimeout(() => window.scrollTo(0, 0), 10);

    catalogStore.currentPageType = CATALOG_PAGE_TYPES.BRAND;
    brandPageResponse.value = data;
  }
}

useHead({
  title: brandPageResponse.value?.data.seo_data.title,
  meta: [
    { name: 'description', content: brandPageResponse.value?.data.seo_data.description },
    { property: 'og:title', content: brandPageResponse.value?.data.seo_data.title },
    { property: 'og:description', content: brandPageResponse.value?.data.seo_data?.description },
    { property: 'og:type', content: 'category' },
    { property: 'og:url', content: route.fullPath },
    { property: 'og:site_name', content: 'Hellride.ru' },
    { property: 'og:locale', content: 'ru_RU' },
    { property: 'og:image', content: brandPageResponse.value?.data.offers[0]?.offer_images[0]?.url },
  ],
});

watch(
  () => route.query,
  async () => {
    const data = await catalogStore.getBrandBySlug();

    await processData(data);
  },
);
</script>

<template>
  <section
    v-if="brandPageResponse"
    class="brand-page"
  >
    <CatalogBreadcrumbsBlock :breadcrumbs="brandPageResponse.data.breadcrumbs" />

    <div class="relative">
      <picture>
        <source
          v-if="brandPageResponse.data.image.conversions_webp"
          :srcset="`${brandPageResponse.data.image.conversions_webp?.thumb_400}, ${brandPageResponse.data.image.conversions_webp?.thumb_800} 2x`"
          media="(max-width: 360px)"
          type="image/webp"
        />

        <source
          v-if="brandPageResponse.data.image.conversions_webp"
          :srcset="`${brandPageResponse.data.image.conversions_webp?.thumb_1400}, ${brandPageResponse.data.image.conversions_webp?.thumb_2800} 2x`"
          type="image/webp"
        />

        <source
          v-if="brandPageResponse.data.image.conversions"
          :srcset="brandPageResponse.data.image.conversions?.thumb_1400"
          type="image/jpeg"
        />

        <img
          :alt="brandPageResponse.data.name"
          :src="brandPageResponse.data.image.conversions?.thumb_1400 || brandPageResponse.data.image.url"
          class="min-h-[100px] object-contain"
        />
      </picture>

      <BrandLogoBlock
        :brand="brandPageResponse.data as Brand"
        class="brand-page__logo"
      />
    </div>

    <FoldingTextBlock
      v-if="brandPageResponse.data.description"
      :max-length="200"
      :text="brandPageResponse.data.description"
      class="text-sm mt-4 sm:mt-5"
    />

    <h1>{{ brandPageResponse.data.name }}</h1>

    <ProductsListing
      :filters="brandPageResponse.data.filters"
      :offers="brandPageResponse.data.offers"
      :pagination="brandPageResponse.data.pagination"
      :schema="brandPageResponse.data.schema"
      :sort-options="brandPageResponse.data.sorting_options"
    />

    <FoldingTextBlock
      v-if="brandPageResponse.data.seo_data?.text"
      :max-length="220"
      :text="brandPageResponse.data.seo_data.text"
      class="text-sm"
    />
  </section>
</template>

<style lang="scss" scoped>
.brand-page {
  gap: 16px;

  @include inner-content;

  &__logo {
    position: absolute;
    bottom: -9px;
    left: 16px;

    @include screen-sm {
      bottom: -24px;
      left: 24px;
    }
  }
}
</style>
